import React from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { useMutation } from '@tanstack/react-query'
import CatalogAPI from 'api/faber/CatalogAPI'
import NormalText from 'common/Typography/NormalText'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const formikInitialValues = {
  catalogId: '',
  currency: '',
  amount: 0,
  list_price: ''
}

const ChangeCatalogPriceSchema = Yup.object().shape({
  currency: Yup.string().required('Currency is required'),
  amount: Yup.number().required('Amount is required')
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const ChangeCatalogPriceForm = ({
  handleClose,
  catalogId
}: {
  handleClose: () => void
  catalogId: string
}) => {
  const faberToken = OrbitSession.get('faberToken')

  const { mutate: changePrice } = useMutation(CatalogAPI.updateCatalogPrice, {
    onSuccess: () => {
      toast.success('Catalog price updated')
      handleClose()
    },
    onError: () => {
      toast.error('Failed to update catalog price')
    }
  })

  const handleFormSubmit = (values: NewCatalogFormikProps) => {
    const allValues = {
      catalog_id: catalogId,
      currency: values.currency,
      unit_amount: Math.round(values.amount * 100),
      list_price: Math.round(Number(values.list_price) * 100).toString(),
      faberToken: faberToken
    }

    changePrice(allValues)
  }

  /**
   * Event handlers
   */
  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={ChangeCatalogPriceSchema}
    >
      {({ submitForm, handleChange, errors, touched, handleBlur }) => (
        <Form>
          <Grid container rowSpacing={2}>
            {/* CHANGE CATALOG PRICE */}
            <Grid item xs={3}>
              <NormalText isBold>Currency</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='currency'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'currency'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.currency && touched.currency)}
                  helperText={
                    errors.currency && touched.currency ? errors.currency : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Amount</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='amount'
                  type='number'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'amount'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.amount && touched.amount)}
                  helperText={
                    errors.amount && touched.amount ? errors.amount : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>List Amount</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>

                <StyledTextField
                  name='list_price'
                  type='text'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'list_price'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.list_price && touched.list_price)}
                  helperText={
                    errors.list_price && touched.list_price ? errors.list_price : null
                  }
                />
              </Box>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={submitForm}>Change price</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface NewCatalogFormikProps {
  catalogId: string
  currency: string
  amount: number
  list_price: string
}
